import { Component, Input, ElementRef } from '@angular/core';
import * as XLSX from 'xlsx';

declare var $: any;

/**
 * Boton reutilizable para descargar a Excel el contenido de un reporte.
 *
 * Genera un archivo .xlsx REAL con SheetJS (Excel lo abre nativo, sin avisos
 * de corrupto). Toma el contenido del reporte de dos formas:
 *   1. Tablas HTML <table> (una hoja por tabla).
 *   2. "Tablas de divs" con clases .row-header / .row-body / .cell (algunos
 *      reportes arman sus tablas con divs); se exportan tambien.
 *
 * Uso:
 *   <app-boton-excel filename="reporte-saldos"></app-boton-excel>
 *
 * Por defecto exporta lo que hay dentro del contenedor ".content-page" del
 * reporte actual (la barra lateral queda fuera). Se puede acotar con
 * [target]="'#mi-zona'".
 */
@Component({
  selector: 'app-boton-excel',
  templateUrl: './boton-excel.component.html'
})
export class BotonExcelComponent {
  @Input() filename: string = 'reporte';
  @Input() target: string;
  @Input() label: string = 'DESCARGAR EXCEL';
  @Input() btnClass: string = 'btn w-100 btn-rounded btn-success';

  constructor(private el: ElementRef) {}

  descargar() {
    const contenedor = this.resolverContenedor();
    if (!contenedor) {
      this.aviso('No se encontro el contenido del reporte para exportar.');
      return;
    }

    try {
      // Todas las tablas/bloques del reporte van apiladas en UNA sola hoja,
      // como se ve el reporte en pantalla (con un titulo por bloque).
      const filas: any[][] = [];
      let algo = false;

      // 1) Tablas HTML reales (una por propietario/seccion)
      const tablas = this.recolectarTablas(contenedor);
      for (let i = 0; i < tablas.length; i++) {
        const aoa = this.tablaToAoa(tablas[i]);
        if (aoa.length) {
          if (algo) { filas.push([]); }
          const titulo = this.tituloDeTabla(tablas[i]);
          if (titulo) { filas.push([titulo]); }
          for (let r = 0; r < aoa.length; r++) { filas.push(aoa[r]); }
          algo = true;
        }
      }

      // 2) Tablas armadas con divs (.row-header / .row-body / .cell)
      const grids = this.recolectarGrids(contenedor);
      for (let i = 0; i < grids.length; i++) {
        const aoa = this.gridToAoa(grids[i]);
        if (aoa.length) {
          if (algo) { filas.push([]); }
          const titulo = this.tituloDeTabla(grids[i]);
          if (titulo) { filas.push([titulo]); }
          for (let r = 0; r < aoa.length; r++) { filas.push(aoa[r]); }
          algo = true;
        }
      }

      if (!algo) {
        this.aviso('No hay informacion para exportar en este reporte.');
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(filas);
      this.autoAncho(ws);
      XLSX.utils.book_append_sheet(wb, ws, 'Reporte');

      const nombre = (this.filename || 'reporte') + '_' + this.fechaHoy() + '.xlsx';
      XLSX.writeFile(wb, nombre);
    } catch (e) {
      this.aviso('No se pudo generar el archivo de Excel.');
    }
  }

  // Convierte una <table> a arreglo de filas (respetando los valores de celda).
  private tablaToAoa(tabla: HTMLTableElement): any[][] {
    const clon = this.clonarLimpia(tabla);
    clon.style.position = 'absolute';
    clon.style.left = '-9999px';
    clon.style.top = '0';
    clon.style.display = 'table';
    document.body.appendChild(clon);
    const ws = XLSX.utils.table_to_sheet(clon);
    document.body.removeChild(clon);
    return XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false }) as any[][];
  }

  // Busca un titulo para el bloque (ej. "Propietario: X") en la tarjeta que contiene la tabla.
  private tituloDeTabla(el: HTMLElement): string {
    const cont = (el.closest ? (el.closest('.card') as HTMLElement) : null) || (el.parentElement as HTMLElement);
    if (!cont) {
      return '';
    }
    const hs: any[] = Array.prototype.slice.call(cont.querySelectorAll('h1,h2,h3,h4,h5'));
    const partes: string[] = [];
    for (let i = 0; i < hs.length && partes.length < 3; i++) {
      const txt = ((hs[i].textContent || '') as string).replace(/\s+/g, ' ').trim();
      if (txt) { partes.push(txt); }
    }
    return partes.join('  |  ');
  }

  private resolverContenedor(): HTMLElement {
    if (this.target) {
      return document.querySelector(this.target) as HTMLElement;
    }
    const host = this.el.nativeElement as HTMLElement;
    const cercano = host && host.closest ? (host.closest('.content-page') as HTMLElement) : null;
    return cercano || (document.querySelector('.content-page') as HTMLElement);
  }

  private recolectarTablas(contenedor: HTMLElement): HTMLTableElement[] {
    const todas: any[] = Array.prototype.slice.call(contenedor.querySelectorAll('table'));
    const resultado: HTMLTableElement[] = [];
    for (let i = 0; i < todas.length; i++) {
      const t = todas[i] as HTMLElement;
      if (t.closest && t.closest('.d-print-none')) {
        continue;
      }
      if (t.querySelectorAll('tr').length > 0) {
        resultado.push(t as HTMLTableElement);
      }
    }
    return resultado;
  }

  private recolectarGrids(contenedor: HTMLElement): HTMLElement[] {
    const cabeceras: any[] = Array.prototype.slice.call(contenedor.querySelectorAll('.row-header'));
    const contenedores: HTMLElement[] = [];
    for (let i = 0; i < cabeceras.length; i++) {
      const h = cabeceras[i] as HTMLElement;
      if (h.closest && h.closest('.d-print-none')) {
        continue;
      }
      const cont = h.parentElement as HTMLElement;
      if (cont && contenedores.indexOf(cont) === -1) {
        contenedores.push(cont);
      }
    }
    return contenedores;
  }

  private gridToAoa(cont: HTMLElement): any[][] {
    const aoa: any[][] = [];
    const filas: any = cont.children;
    for (let i = 0; i < filas.length; i++) {
      const fila = filas[i] as HTMLElement;
      const cls = fila.className || '';
      if (cls.indexOf('row-header') < 0 && cls.indexOf('row-body') < 0) {
        continue;
      }
      const celdas: any[] = [];
      const hijos: any = fila.children;
      for (let j = 0; j < hijos.length; j++) {
        const c = hijos[j] as HTMLElement;
        if ((c.className || '').indexOf('cell') >= 0) {
          celdas.push(this.valorCelda(c.textContent || ''));
        }
      }
      if (celdas.length) {
        aoa.push(celdas);
      }
    }
    return aoa;
  }

  private autoAncho(ws: any) {
    const ref = ws['!ref'];
    if (!ref) {
      return;
    }
    const rango = XLSX.utils.decode_range(ref);
    const cols: any[] = [];
    for (let C = rango.s.c; C <= rango.e.c; C++) {
      let max = 8;
      for (let R = rango.s.r; R <= rango.e.r; R++) {
        const dir = XLSX.utils.encode_cell({ r: R, c: C });
        const celda = ws[dir];
        if (celda && celda.v !== undefined && celda.v !== null) {
          const txt = (celda.w !== undefined && celda.w !== null) ? celda.w : celda.v;
          const largo = String(txt).length;
          if (largo > max) {
            max = largo;
          }
        }
      }
      cols.push({ wch: Math.min(max + 2, 60) });
    }
    ws['!cols'] = cols;
  }

  private valorCelda(texto: string): any {
    const limpio = (texto || '').replace(/\s+/g, ' ').trim();
    if (limpio === '') {
      return '';
    }
    const num = limpio.replace(/\$/g, '').replace(/,/g, '').trim();
    if (/^-?\d+(\.\d+)?$/.test(num)) {
      const n = parseFloat(num);
      if (!isNaN(n)) {
        return n;
      }
    }
    return limpio;
  }

  private clonarLimpia(tabla: HTMLTableElement): HTMLTableElement {
    const clon = tabla.cloneNode(true) as HTMLElement;
    const basura: any[] = Array.prototype.slice.call(
      clon.querySelectorAll('.d-print-none, button, .btn, input, select, textarea, app-ajax-loader, i, svg')
    );
    for (let i = 0; i < basura.length; i++) {
      const n = basura[i] as HTMLElement;
      if (n.parentNode) {
        n.parentNode.removeChild(n);
      }
    }
    return clon as HTMLTableElement;
  }

  private fechaHoy(): string {
    const d = new Date();
    const mm = ('0' + (d.getMonth() + 1)).slice(-2);
    const dd = ('0' + d.getDate()).slice(-2);
    return d.getFullYear() + '-' + mm + '-' + dd;
  }

  private aviso(mensaje: string) {
    try {
      $.NotificationApp.send('Aviso', mensaje, 'top-right', 'rgba(0,0,0,0.2)', 'warning');
    } catch (e) {
      alert(mensaje);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/tools/boton-excel/boton-excel.component.ts